import { Field, Formik, Form as FormikForm } from 'formik';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineWhatsApp
} from 'react-icons/ai';
import { BiBuildingHouse, BiTime } from 'react-icons/bi';
import { BsHouseDoor } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { updateBranchOffice } from 'redux/branchOfficeSlice';
import ButtonPrimarySubmit from 'utils/buttons/ButtonPrimarySubmit';
import { formSchema } from '../validations/formSchema';
import { useState } from 'react';

const inputs = [
  {
    label: 'Dirección',
    name: 'address',
    Icon: BsHouseDoor,
    placeholder: 'Ingrese una dirección',
    isDisabled: true,
    type: 'text'
  },
  {
    label: 'Barrio',
    name: 'barrio',
    Icon: BiBuildingHouse,
    placeholder: 'Seleccione un barrio',
    type: 'select'
  },
  {
    label: 'Email',
    name: 'email',
    Icon: AiOutlineMail,
    placeholder: 'Ingrese un email',
    type: 'text'
  },
  {
    label: 'Horarios de atención',
    name: 'openingHours',
    Icon: BiTime,
    placeholder: 'Seleccione los horarios de atención',
    type: 'text'
  },
  {
    label: 'Teléfono',
    name: 'phoneNumber',
    Icon: AiOutlinePhone,
    placeholder: 'Ingrese un teléfono',
    isDisabled: true,
    type: 'text'
  },
  {
    label: 'Whatsapp',
    name: 'whatsapp',
    Icon: AiOutlineWhatsApp,
    placeholder: 'Ingrese un número de whatsapp',
    type: 'text'
  },
  {
    label: 'Teléfonos adicionales',
    name: 'extraPhones',
    Icon: AiOutlinePhone,
    placeholder: 'Ingrese teléfonos adicionales',
    type: 'extraPhones'
  }
];

const getInput = ({
  type,
  name,
  touched,
  errors,
  isDisabled,
  placeholder,
  values,
  setValues
}) => {
  const { neighborhoods } = useSelector(state => state.props);
  const [isError, setIsError] = useState(false);

  switch (type) {
    case 'select':
      return (
        <Select
          name={name}
          value={neighborhoods.find(option => option.value === values[name])}
          onChange={option => {
            setValues({
              ...values,
              [name]: option ? option.value : ''
            });
          }}
          options={neighborhoods.map(neighborhood => ({
            value: neighborhood.value,
            label: neighborhood.label
          }))}
          placeholder={placeholder}
          isDisabled={isDisabled}
          styles={{
            menu: base => ({ ...base, zIndex: 9999 }),
            control: base => ({
              ...base,
              borderColor:
                touched[name] && !!errors[name] ? 'red' : base.borderColor,
              boxShadow:
                touched[name] && !!errors[name]
                  ? '0 0 0 1px red'
                  : base.boxShadow
            })
          }}
        />
      );

    case 'extraPhones':
      return (
        <>
          <Creatable
            classNames={{
              control: () => 'form-control',
              dropdownIndicator: () => 'd-none',
              clearIndicator: () => 'd-none',
              indicatorSeparator: () => 'd-none',
              input: () => 'p-0',
              valueContainer: () => 'p-0'
            }}
            classNamePrefix="react-select"
            isMulti
            placeholder={placeholder}
            noOptionsMessage={() => 'Escriba para crear un nuevo teléfono'}
            formatCreateLabel={inputValue => `Crear teléfono: ${inputValue}`}
            value={values.extraPhones}
            onChange={selected => {
              const isLong = selected.some(
                phone => phone.label.length < 8 || phone.label.length > 13
              );
              if (isLong) {
                setIsError(true);
                setTimeout(() => {
                  setIsError(false);
                }, 5000);
              } else {
                setValues({
                  ...values,
                  extraPhones: selected
                });
              }
            }}
          />
          {isError && (
            <div
              style={{
                marginTop: '0.25rem',
                fontSize: '75%',
                color: '#e63757'
              }}
            >
              Los números deben tener entre 8 y 13 dígitos
            </div>
          )}
        </>
      );

    default:
      return (
        <Form.Control
          as={Field}
          name={name}
          isInvalid={touched[name] && !!errors[name]}
          disabled={isDisabled}
          type={type}
          placeholder={placeholder}
        />
      );
  }
};

const InputBranchOffice = ({
  label,
  name,
  Icon,
  placeholder,
  touched,
  errors,
  isDisabled,
  type,
  options,
  values,
  setValues
}) => (
  <Col xs={12}>
    <Form.Group>
      <Form.Label>
        <span className="logo-normal">
          <Icon /> {label}
        </span>
      </Form.Label>

      {getInput({
        type,
        name,
        touched,
        errors,
        isDisabled,
        options,
        placeholder,
        values,
        setValues
      })}

      <Form.Control.Feedback type="invalid">
        {errors[name]}
      </Form.Control.Feedback>
    </Form.Group>
  </Col>
);

InputBranchOffice.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  placeholder: PropTypes.string.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  type: PropTypes.string.isRequired,
  options: PropTypes.array,
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired
};

export const BranchOfficeForm = ({ branchOffice }) => {
  const dispatch = useDispatch();

  const initialValues = {
    address: branchOffice.address || '',
    barrio: branchOffice.barrio || '',
    email: branchOffice.email || '',
    openingHours: branchOffice.openingHours || '',
    extraPhones: branchOffice.extraPhones,
    phoneNumber: branchOffice.phoneNumber || '',
    whatsapp: branchOffice.whatsapp || ''
  };

  return (
    <Card className="bg-white">
      <Card.Header className="border-bottom">
        <Card.Title>{branchOffice.branch_office_name}</Card.Title>
      </Card.Header>

      <Card.Body>
        <Formik
          initialValues={initialValues}
          onSubmit={values => {
            dispatch(updateBranchOffice(branchOffice.id, values));
          }}
          validationSchema={formSchema}
        >
          {({ errors, touched, setValues, values }) => {
            useEffect(() => {
              setValues(initialValues);
            }, [branchOffice]);

            return (
              <FormikForm>
                <Row className="g-3">
                  {inputs.map((input, index) => (
                    <Col xs={12} key={index}>
                      <InputBranchOffice
                        {...input}
                        touched={touched}
                        errors={errors}
                        setValues={setValues}
                        values={values}
                      />
                    </Col>
                  ))}

                  <Col xs={12} className="text-muted fs--2">
                    * Los datos de dirección y teléfono solo pueden modificarse
                    a través del departamento de Atención al Matriculado. Para
                    consultas enviar un mail a:
                    cabaprop@colegioinmobiliario.org.ar
                  </Col>

                  <Col xs={12}>
                    <ButtonPrimarySubmit
                      text="Guardar cambios"
                      className="w-100"
                    />
                  </Col>
                </Row>
              </FormikForm>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};
BranchOfficeForm.propTypes = {
  branchOffice: PropTypes.object.isRequired
};
